import React from 'react';
import { BankOutlined, CheckCircleOutlined, ClockCircleOutlined, FieldTimeOutlined, MessageOutlined, PhoneOutlined, RobotOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import { ContextStatus } from '@leedpulse-monorepo/enums';
import { formatNumber } from '@leedpulse-monorepo/utils';
import StatsTile from '../../@shared/StatsTile';
import { AI_RECEPTIONIST_VIOLET, CATEGORICAL_COLORS } from './chartColors';
import type { OverviewBucket, OverviewStats } from '../../../../domain/use_cases/leedflow/overview/getOverviewStats';

// Même raison que `callCells.tsx` : `commonUtils` ne pose `dayjs.locale('fr')`
// qu'à l'intérieur d'une de ses fonctions, rien ne garantit qu'elle ait été
// appelée avant ce rendu, et l'infobulle de comparaison afficherait
// « saturday 5 september ».
dayjs.locale('fr');

interface KpiRowProps {
  stats: OverviewStats;
  /** Fenêtre de comparaison telle que le backend l'a calculée : agrégat + bornes. */
  previous: OverviewStats['previous'];
  status: ContextStatus;
  /** Masque uniquement la tuile de coût, comme le faisait l'ancien `CostStats.tsx`. */
  isConfidential?: boolean;
}

// Hauteur fixe des huit tuiles, imposée sur le conteneur plutôt que sur
// StatsTile (non modifiable ici) : la revue de la Task 6 a relevé qu'une
// tuile portant écart + sparkline grandit alors que ses voisines sans les
// deux restent basses, ce qui donnerait des lignes de grille inégales dès
// que ce bandeau active les deux props sur certaines tuiles seulement.
// `display: flex` sur le wrapper étire la Card (qui ne fixe pas sa propre
// hauteur) jusqu'à cette valeur, qu'elle porte un écart/une sparkline ou non.
//
// Valeur calée sur la tuile la plus chargée (titre + valeur + écart +
// sparkline, ex. « Appels traités ») : padding du body (16 haut + 16 bas =
// 32) + titre (~24) + valeur en 32px/line-height 1 (~32) + écart (~13px,
// marginTop 2 ≈ 20) + sparkline (height 40 + marginTop 6 = 46). Total
// ≈ 154 px. 160 ne laissait que 6 px de marge — trop juste face à des
// écarts de rendu de police/ApexCharts d'un navigateur à l'autre. 184
// px redonne une vraie marge (~30 px) sans viser un calcul pixel-perfect.
const TILE_HEIGHT = 184;

// L'écart n'est calculé que si le backend a jugé la période précédente
// significative — sous 10 appels traités il n'envoie pas `previous`. Ne
// jamais fabriquer un écart de zéro dans ce cas.
const delta = (current: number, prev: number | undefined): { value: number } | undefined =>
  prev === undefined || prev === 0 ? undefined : { value: (current - prev) / prev };

// Séries dérivées de `buckets`, honnêtes seulement pour ces quatre métriques :
// - handled : `bucket.handled`, incrémenté pour CHAQUE appel non-défaut dans
//   `buildBuckets` (backend/src/helpers/leedflow/overview-model.ts), exactement
//   comme `handledCalls` dans l'agrégat. PAS `inbound + outbound` : un doc à
//   `direction: null` compte dans `handled` mais dans aucun des deux, ce qui
//   sous-comptait silencieusement la sparkline par rapport à la valeur
//   affichée à côté (cf. overview-model.test.ts, invariant `handled`).
// - taux de décroché / durée moyenne / minutes totales : mêmes prédicats que
//   l'agrégat (`answered`/`missed`/`durationSeconds`), un point par bucket où
//   le dénominateur existe. Aucune série n'est dérivable pour SMS, appels en
//   défaut ou coût (buckets ne portent ni SMS, ni défauts, ni prix) — ces
//   tuiles ne reçoivent donc pas de `sparkline`.
const handledSeries = (buckets: OverviewBucket[]): number[] =>
  buckets.map((bucket) => bucket.handled);

const answerRateSeries = (buckets: OverviewBucket[]): number[] =>
  buckets
    .filter((bucket) => bucket.answered + bucket.missed > 0)
    .map((bucket) => bucket.answered / (bucket.answered + bucket.missed));

/**
 * « 2 min 22 s » plutôt que « 2.37 min ».
 *
 * Un décimal de minute ne se lit pas : personne ne convertit 0,37 min en tête,
 * alors qu'une durée d'appel se pense en minutes et secondes. Sous la minute,
 * les minutes disparaissent — « 0 min 42 s » ferait lire un zéro pour rien.
 */
const formatDuration = (seconds: number): string => {
  const total = Math.max(0, Math.round(seconds));
  const minutes = Math.floor(total / 60);
  const rest = total % 60;
  return minutes > 0 ? `${formatNumber(minutes)} min ${rest} s` : `${rest} s`;
};

const averageDurationSeries = (buckets: OverviewBucket[]): number[] =>
  buckets
    .filter((bucket) => bucket.answered > 0)
    .map((bucket) => bucket.durationSeconds / 60 / bucket.answered);

// Minutes décrochées par bucket. Contrairement à la moyenne juste au-dessus,
// aucun filtre : un bucket sans appel décroché vaut zéro minute, ce qui est
// l'information, pas un trou à masquer.
const totalDurationSeries = (buckets: OverviewBucket[]): number[] =>
  buckets.map((bucket) => bucket.durationSeconds / 60);

/**
 * Contre quoi l'écart se lit, en clair. La fenêtre vient du backend
 * (`previousPeriod`) et n'est pas la même selon la profondeur de la
 * sélection : jusqu'à 7 jours il recule d'une semaine pile, donc « samedi »
 * se compare à « samedi », et un « -64,3 % » sans cette phrase laisse croire
 * à une comparaison avec la veille.
 *
 * Une fenêtre qui ne finit pas à minuit est une période en cours, bornée par
 * le backend à la durée écoulée : le dire évite de faire lire une journée
 * entière là où il n'y a que la matinée.
 */
const comparisonLabel = (window: { from: number; to: number } | undefined): string | undefined => {
  if (!window) return undefined;
  const from = dayjs(window.from);
  const to = dayjs(window.to);
  const range = from.isSame(to, 'day')
    ? from.format('dddd D MMMM')
    : `${from.format('D MMM')} → ${to.format('D MMM')}`;
  const truncated = !to.isSame(to.endOf('day'), 'second');
  return `Comparé à ${range}${truncated ? `, jusqu'à ${to.format('HH:mm')}` : ''}`;
};

const KpiRow: React.FC<KpiRowProps> = ({ stats, previous, status, isConfidential = false }) => {
  const { callsStats, smsStats, costStats, voicemailStats, buckets } = stats;
  const previousStats = previous?.callsStats;
  const comparison = comparisonLabel(previous?.period);

  // `answerRate` est déjà un pourcentage : un `delta` fractionnaire dessus
  // afficherait un pourcentage DE pourcentage (50 % → 60 % rendu "+20 %", lu
  // comme +20 points par tout lecteur). L'écart de cette tuile s'exprime donc
  // en points de pourcentage, dans `info`, jamais via `delta`.
  const answerRatePointsDelta =
    previousStats?.answerRate !== undefined
      ? (callsStats.answerRate - previousStats.answerRate) * 100
      : undefined;

  const items = [
    {
      icon: <PhoneOutlined />,
      title: 'Appels traités',
      value: formatNumber(callsStats.handledCalls),
      delta: delta(callsStats.handledCalls, previousStats?.handledCalls),
      comparison,
      sparkline: handledSeries(buckets),
    },
    {
      icon: <CheckCircleOutlined />,
      title: 'Taux de décroché',
      value: `${formatNumber(parseFloat((callsStats.answerRate * 100).toFixed(1)))}%`,
      // Pas de `delta` ici : voir le commentaire sur `answerRatePointsDelta`
      // ci-dessus. L'écart en points de pourcentage part dans `info`.
      //
      // « pts » seul, sans « vs période précédente » : les autres tuiles
      // n'écrivent pas non plus contre quoi elles se comparent, et la phrase
      // entière repassait à la ligne au milieu, coupée entre « période » et
      // « précédente ». Un écart se lit d'un bloc ou ne se lit pas.
      info:
        answerRatePointsDelta !== undefined
          ? `${answerRatePointsDelta >= 0 ? '+' : ''}${formatNumber(parseFloat(answerRatePointsDelta.toFixed(1)))} pts`
          : undefined,
      comparison,
      sparkline: answerRateSeries(buckets),
    },
    {
      icon: <ClockCircleOutlined />,
      title: 'Durée moyenne',
      value: formatDuration(callsStats.averageDurationSeconds),
      delta: delta(callsStats.averageDurationMinutes, previousStats?.averageDurationMinutes),
      comparison,
      sparkline: averageDurationSeries(buckets),
    },
    {
      icon: <FieldTimeOutlined />,
      // Le volume de conversation de la période, que ni le nombre d'appels ni
      // la moyenne ne donnent : 600 appels d'une minute et 300 de deux minutes
      // affichent le même total, et c'est ce total qui se compare à un forfait.
      // Affiché en minutes entières, l'unité dans laquelle la téléphonie se
      // facture — la seconde n'a pas de sens sur un cumul de cette taille.
      title: 'Minutes totales',
      value: `${formatNumber(Math.round(callsStats.totalDurationSeconds / 60))} min`,
      delta: delta(callsStats.totalDurationSeconds, previousStats?.totalDurationSeconds),
      comparison,
      sparkline: totalDurationSeries(buckets),
    },
    {
      icon: <MessageOutlined />,
      title: 'SMS',
      value: formatNumber(smsStats.sentCount + smsStats.receivedCount),
      // Mêmes deux verts que les anneaux de répartition : envoyé et reçu sont
      // les deux sens d'une même chose, comme entrant et sortant pour un appel.
      split: [
        { label: `${formatNumber(smsStats.sentCount)} envoyés`, value: smsStats.sentCount, color: CATEGORICAL_COLORS[0] },
        { label: `${formatNumber(smsStats.receivedCount)} reçus`, value: smsStats.receivedCount, color: CATEGORICAL_COLORS[1] },
      ],
      // Pas d'écart : `previous` ne porte que `callsStats` (D3).
    },
  ];

  // Les trois coûts, dans le même bandeau que le reste mais groupés à la fin :
  // un montant se compare à un autre montant, et les garder contigus suffit à
  // les faire lire ensemble. La somme des trois vaut exactement l'ancien
  // « tout compris ».
  const costItems = [
    {
      icon: <BankOutlined />,
      // La messagerie vocale est comptée ici : c'est la fin d'un appel entrant,
      // pas une quatrième nature de dépense. Le détail part dans `split` pour
      // que le rattachement reste visible plutôt que caché dans un total.
      title: 'Coût appels',
      value: `${formatNumber(parseFloat((callsStats.totalCost + voicemailStats.totalCost).toFixed(2)))}€`,
      split: voicemailStats.totalCost > 0
        ? [
          { label: `${formatNumber(parseFloat(callsStats.totalCost.toFixed(2)))}€ appels`, value: callsStats.totalCost, color: CATEGORICAL_COLORS[0] },
          { label: `${formatNumber(parseFloat(voicemailStats.totalCost.toFixed(2)))}€ messagerie`, value: voicemailStats.totalCost, color: CATEGORICAL_COLORS[1] },
        ]
        : undefined,
      confidential: isConfidential,
    },
    {
      icon: <MessageOutlined />,
      title: 'Coût SMS',
      value: `${formatNumber(parseFloat(smsStats.totalCost.toFixed(2)))}€`,
      // Mêmes deux verts et mêmes mots que la tuile de volume juste au-dessus :
      // « envoyés » et « reçus » y désignent déjà ces deux sens. Le découpage
      // vaut la place qu'il prend, l'envoi coûte bien plus cher à l'unité que
      // la réception.
      split: smsStats.sentCost + smsStats.receivedCost > 0
        ? [
          { label: `${formatNumber(parseFloat(smsStats.sentCost.toFixed(2)))}€ envoyés`, value: smsStats.sentCost, color: CATEGORICAL_COLORS[0] },
          { label: `${formatNumber(parseFloat(smsStats.receivedCost.toFixed(2)))}€ reçus`, value: smsStats.receivedCost, color: CATEGORICAL_COLORS[1] },
        ]
        : undefined,
      confidential: isConfidential,
    },
    {
      icon: <RobotOutlined />,
      title: 'Coût IA',
      value: `${formatNumber(parseFloat((costStats.openaiCost + costStats.elevenlabsCost).toFixed(2)))}€`,
      // Deux fournisseurs, deux natures de dépense : le texte et la voix. Les
      // séparer ici évite d'avoir à ouvrir la base pour savoir lequel dérape.
      split: costStats.openaiCost + costStats.elevenlabsCost > 0
        ? [
          { label: `${formatNumber(parseFloat(costStats.openaiCost.toFixed(2)))}€ OpenAI`, value: costStats.openaiCost, color: CATEGORICAL_COLORS[3] },
          { label: `${formatNumber(parseFloat(costStats.elevenlabsCost.toFixed(2)))}€ ElevenLabs`, value: costStats.elevenlabsCost, color: AI_RECEPTIONIST_VIOLET },
        ]
        : undefined,
      confidential: isConfidential,
    },
  ];

  // `confidential` n'existe que sur certaines tuiles : le type de StatsTile
  // sert de contrat commun aux deux grilles, l'inférence sur les tableaux
  // perdrait la propriété dès qu'aucun élément de l'un ne la porte.
  type TileItem = React.ComponentProps<typeof StatsTile>['item'] & { confidential?: boolean };

  /** Rend une grille de tuiles. */
  const renderGrid = (tiles: TileItem[]) => (
      <div className="overview-page-stats">
        {tiles.map((item, index) => (
          <div key={index} style={{ height: TILE_HEIGHT, display: 'flex' }}>
            <StatsTile isLoading={status === ContextStatus.LOADING} isConfidential={item.confidential ?? false} item={item} />
          </div>
        ))}
      </div>
  );

  // Une seule grille : huit tuiles toutes de même largeur, soit quatre lignes
  // pleines sur deux colonnes. « Appels traités » n'occupe plus les deux
  // colonnes — c'était le remplissage de la demi-ligne laissée par sept tuiles,
  // et la huitième la comble désormais d'elle-même. Deux grilles séparées
  // forçaient un retour à la ligne avant les coûts et laissaient une demi-ligne
  // blanche des deux côtés.
  return renderGrid([...items, ...costItems]);
};

export default KpiRow;
